import { useState } from 'react';
import { useClient, useQuery } from 'urql';
import { Plus, RefreshCw, Search } from 'lucide-react';
import PageHeader from '@/components/PageHeader.jsx';
import { useToast } from '@/hooks/useKeyboardShortcuts.js';
import { GET_ASIENTO, LIST_ASIENTOS } from './gql.js';
import AsientoForm from './Form.jsx';
import AsientoDetail from './Detail.jsx';

export default function AsientosList() {
  const toast = useToast();
  const client = useClient();
  const [filters, setFilters] = useState({ fechaDesde: '', fechaHasta: '', estado: '', origen: '', search: '' });
  const [selectedId, setSelectedId] = useState(null);
  const [mode, setMode] = useState('view');
  const [editingAsiento, setEditingAsiento] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [{ data, fetching }, reexecute] = useQuery({
    query: LIST_ASIENTOS,
    variables: {
      limit: 20,
      offset: 0,
      fechaDesde: filters.fechaDesde || null,
      fechaHasta: filters.fechaHasta || null,
      estado: filters.estado || null,
      origen: filters.origen || null,
      search: filters.search || null,
    },
  });

  const asientos = data?.listAsientos?.items ?? [];
  const total = data?.listAsientos?.pageInfo?.total ?? 0;

  const handleRefresh = () => {
    reexecute({ requestPolicy: 'network-only' });
  };

  const handleSelect = (asiento) => {
    setSelectedId(asiento.id);
    setMode('view');
  };

  const handleCreated = () => {
    toast.success('Asiento guardado');
    setMode('view');
    setEditingAsiento(null);
    setEditError('');
    handleRefresh();
  };

  const handleEdit = async (asientoId) => {
    if (!asientoId) return;
    setSelectedId(asientoId);
    setMode('edit');
    setEditError('');
    setEditLoading(true);
    setEditingAsiento(null);
    try {
      const result = await client.query(GET_ASIENTO, { id: asientoId }).toPromise();
      if (result.error) {
        throw result.error;
      }
      const asientoCompleto = result.data?.getAsiento;
      if (!asientoCompleto) {
        throw new Error('No encontramos la información del asiento.');
      }
      setEditingAsiento(asientoCompleto);
    } catch (error) {
      const message = error.message ?? 'No pudimos abrir el asiento para editar.';
      setEditError(message);
      toast.error(message);
      setMode('view');
    } finally {
      setEditLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setMode('view');
    setEditingAsiento(null);
    setEditError('');
  };

  return (
    <div className="min-h-screen bg-[rgba(var(--bg),0.95)] pb-16">
      <div className="mx-auto max-w-[1400px] px-4 pb-10 pt-8">
        <PageHeader
          title="Asientos Contables"
          subtitle="Registra y controla los movimientos manuales"
          actions={[
            <button
              key="refresh"
              type="button"
              onClick={handleRefresh}
              className="flex items-center gap-2 rounded-xl border border-[rgb(var(--border))] px-3 py-2 text-sm text-[rgb(var(--fg))] transition-colors hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
            >
              <RefreshCw className="h-4 w-4" />
              Actualizar
            </button>,
            <button
              key="create"
              type="button"
              onClick={() => setMode('create')}
              className="flex items-center gap-2 rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[rgb(var(--brand-700))] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(var(--ring))]"
            >
              <Plus className="h-4 w-4" />
              Nuevo asiento
            </button>,
          ]}
        />

        <div className="grid grid-cols-12 gap-6">
          <section className="col-span-12 md:col-span-6 space-y-4">
            <div className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.85)] p-4 shadow-[0_18px_34px_-20px_rgba(var(--shadow),0.45)]">
              <div className="grid grid-cols-12 gap-3">
                <div className="col-span-12">
                  <label className="flex items-center gap-2 text-sm font-medium text-[rgb(var(--fg))]">
                    <Search className="h-4 w-4 text-[rgb(var(--muted))]" />
                    Buscar
                  </label>
                  <input
                    type="search"
                    value={filters.search}
                    onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
                    placeholder="Número, glosa o referencia"
                    className="mt-1 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.9)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                  />
                </div>
                <div className="col-span-6">
                  <label className="text-xs font-medium uppercase text-[rgb(var(--muted))]">Desde</label>
                  <input
                    type="date"
                    value={filters.fechaDesde}
                    onChange={(event) => setFilters((prev) => ({ ...prev, fechaDesde: event.target.value }))}
                    className="mt-1 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.9)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                  />
                </div>
                <div className="col-span-6">
                  <label className="text-xs font-medium uppercase text-[rgb(var(--muted))]">Hasta</label>
                  <input
                    type="date"
                    value={filters.fechaHasta}
                    onChange={(event) => setFilters((prev) => ({ ...prev, fechaHasta: event.target.value }))}
                    className="mt-1 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.9)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                  />
                </div>
                <div className="col-span-6">
                  <label className="text-xs font-medium uppercase text-[rgb(var(--muted))]">Estado</label>
                  <select
                    value={filters.estado}
                    onChange={(event) => setFilters((prev) => ({ ...prev, estado: event.target.value }))}
                    className="mt-1 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.9)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                  >
                    <option value="">Todos</option>
                    <option value="borrador">Borrador</option>
                    <option value="confirmado">Confirmado</option>
                  </select>
                </div>
                <div className="col-span-6">
                  <label className="text-xs font-medium uppercase text-[rgb(var(--muted))]">Origen</label>
                  <select
                    value={filters.origen}
                    onChange={(event) => setFilters((prev) => ({ ...prev, origen: event.target.value }))}
                    className="mt-1 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.9)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                  >
                    <option value="">Todos</option>
                    <option value="manual">Manual</option>
                    <option value="ventas">Ventas</option>
                    <option value="compras">Compras</option>
                    <option value="tesoreria">Tesorería</option>
                    <option value="stock">Stock</option>
                  </select>
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={handleRefresh}
                  className="rounded-xl border border-[rgb(var(--border))] px-4 py-2 text-sm text-[rgb(var(--fg))] transition-colors hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                >
                  Aplicar filtros
                </button>
              </div>
            </div>

            <div className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.8)] shadow-[0_18px_34px_-20px_rgba(var(--shadow),0.45)]">
              <header className="flex items-center justify-between border-b border-[rgb(var(--border))] px-4 py-3 text-sm text-[rgb(var(--muted))]">
                <span>{fetching ? 'Cargando asientos...' : `${total} asientos`}</span>
              </header>
              <div className="max-h-[580px] overflow-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-[rgba(var(--panel),0.95)] text-[rgb(var(--muted))]">
                    <tr>
                      <th className="px-4 py-2 text-left">Fecha</th>
                      <th className="px-4 py-2 text-left">Número</th>
                      <th className="px-4 py-2 text-left">Origen</th>
                      <th className="px-4 py-2 text-left">Glosa</th>
                      <th className="px-4 py-2 text-right">Debe</th>
                      <th className="px-4 py-2 text-right">Haber</th>
                      <th className="px-4 py-2 text-left">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgb(var(--border))]">
                    {asientos.map((asiento) => {
                      const selected = asiento.id === selectedId;
                      return (
                        <tr
                          key={asiento.id}
                          className={`cursor-pointer transition-colors ${
                            selected ? 'bg-[rgba(var(--brand),0.15)]' : 'hover:bg-[rgba(var(--muted),0.12)]'
                          }`}
                          onClick={() => handleSelect(asiento)}
                        >
                          <td className="px-4 py-3">{asiento.fecha}</td>
                          <td className="px-4 py-3 font-medium text-[rgb(var(--fg))]">{asiento.numero}</td>
                          <td className="px-4 py-3 text-[rgb(var(--muted))]">{asiento.origen}</td>
                          <td className="px-4 py-3">{asiento.glosa || '—'}</td>
                          <td className="px-4 py-3 text-right text-[rgb(var(--fg))]">{asiento.totales?.debe?.toFixed(2)}</td>
                          <td className="px-4 py-3 text-right text-[rgb(var(--fg))]">{asiento.totales?.haber?.toFixed(2)}</td>
                          <td className="px-4 py-3 text-[rgb(var(--muted))]">{asiento.estado}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {!asientos.length && !fetching ? (
                  <div className="px-4 py-6 text-center text-sm text-[rgb(var(--muted))]">No se encontraron asientos.</div>
                ) : null}
              </div>
            </div>
          </section>

          <section className="col-span-12 md:col-span-6 space-y-4">
            {mode === 'create' ? (
              <AsientoForm onSuccess={handleCreated} onCancel={() => setMode('view')} />
            ) : mode === 'edit' && selectedId ? (
              editLoading ? (
                <div className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.65)] p-6 text-sm text-[rgb(var(--muted))]">
                  Cargando asiento para edición...
                </div>
              ) : editingAsiento ? (
                <AsientoForm asiento={editingAsiento} onSuccess={handleCreated} onCancel={handleCancelEdit} />
              ) : (
                <div className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.65)] p-6 text-sm text-red-500">
                  {editError || 'Seleccioná un asiento para editar.'}
                </div>
              )
            ) : (
              <AsientoDetail asientoId={selectedId} onUpdated={handleRefresh} onEdit={handleEdit} />
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
